import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Générer ou récupérer un ID de session unique
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('visitorSessionId');
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('visitorSessionId', sessionId);
  }
  return sessionId;
};

export const useVisitorTracking = (customerId = null) => {
  const location = useLocation();
  const heartbeatInterval = useRef(null);
  const sessionId = useRef(getSessionId());
  const lastPage = useRef('');

  // Envoyer les données de visite
  const trackVisit = useCallback(async (page) => {
    try {
      const trackingData = {
        sessionId: sessionId.current,
        customerId: customerId,
        page: page,
        referrer: document.referrer || null,
        userAgent: navigator.userAgent,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      await fetch(`${API_URL}/api/tracking/visit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(trackingData)
      });
    } catch (error) {
      // Silently fail - tracking shouldn't break the app
      console.debug('Tracking error:', error);
    }
  }, [customerId]);

  // Heartbeat pour mettre à jour le temps de session
  const sendHeartbeat = useCallback(async () => {
    try {
      await fetch(`${API_URL}/api/tracking/heartbeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionId.current,
          page: lastPage.current
        })
      });
    } catch (error) {
      console.debug('Heartbeat error:', error);
    }
  }, []);

  // Fin de session
  const endSession = useCallback(async () => {
    try {
      await fetch(`${API_URL}/api/tracking/end-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: sessionId.current })
      });
    } catch (error) {
      console.debug('End session error:', error);
    }
  }, []);

  // Tracker les changements de page
  useEffect(() => {
    const currentPage = location.pathname + location.search;
    if (currentPage !== lastPage.current) {
      lastPage.current = currentPage;
      trackVisit(currentPage);
    }
  }, [location, trackVisit]);

  // Démarrer le heartbeat
  useEffect(() => {
    // Premier heartbeat après 30 secondes
    const initialTimeout = setTimeout(() => {
      sendHeartbeat();
      // Puis toutes les 30 secondes
      heartbeatInterval.current = setInterval(sendHeartbeat, 30000);
    }, 30000);

    // Gérer la fermeture de page
    const handleBeforeUnload = () => {
      endSession();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        sendHeartbeat();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(initialTimeout);
      if (heartbeatInterval.current) {
        clearInterval(heartbeatInterval.current);
      }
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [sendHeartbeat, endSession]);

  return { sessionId: sessionId.current };
};

export default useVisitorTracking;
